import type { ReactNode } from 'react'
import type { useVerbisLebenslaufUpload } from './useVerbisLebenslaufUpload'
import {
  Button,
  Confirmation,
  FormScroll,
  FormSection,
  Icon,
  KiLabel,
  StackList,
  StackListItem,
  StatusLine,
} from 'admination-react'
import {
  VERBIS_LEBENSLAUF_ACCEPT,
  type VerbisLebenslaufArt,
  type VerbisLebenslaufHinweis,
} from '../../api/verbisLebenslauf'
import { DropCard } from '@/core/components/modal/DropCard'
import type { LebenslaufOhneDatum, VerbisLebenslaufDone } from './useVerbisLebenslaufStatus'
import { keinBildText } from './dateien'
import { LebenslaufHinweise, type ZeitraumSpeichern } from './hinweise'

export type VerbisLebenslaufEintrag = {
  id: number
  file: File
  art: VerbisLebenslaufArt | null
}

export const HOCHLADEN_TEXT =
  "VerBIS-Screenshots und Lebenslauf werden hochgeladen. Sie können dieses Fenster schließen und mit der nächsten Person weitermachen."

const ART_TEXT: Record<VerbisLebenslaufArt, string> = {
  verbis: "VerBIS-Screenshot",
  lebenslauf: "Lebenslauf-Seite",
}

function groesseText(bytes: number): string {
  if (bytes < 1024) return `${bytes}\u00a0B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)}\u00a0KB`
  return `${(kb / 1024).toFixed(1).replace('.', ',')}\u00a0MB`
}

export type VerbisLebenslaufBereichProps = {
  done: VerbisLebenslaufDone
  statusLoading: boolean
  uploading: boolean
  eintraege: readonly VerbisLebenslaufEintrag[]
  hinweise: readonly VerbisLebenslaufHinweis[]
  ohneDatum: readonly LebenslaufOhneDatum[]
  onAdd: (files: File[]) => void
  onRemove: (id: number) => void
  onSaveZeitraum: ZeitraumSpeichern
  folgt?: boolean
  actions?: ReactNode
}

export const UEBERSPRINGEN_TEXT = "Keine VerBIS-Daten vorhanden"

export function VerbisLebenslaufUploadAction({ upload }: { upload: ReturnType<typeof useVerbisLebenslaufUpload> }) {
  if (upload.statusError || (upload.done.verbis && upload.done.lebenslauf)) return null
  return (
    <div className="pill">
      <Button primaryAction disabled={upload.uploading || upload.statusLoading} onClick={() => void upload.upload()}>
        <Icon name="i-upload" />
        {upload.uploading
          ? "Die Screenshots werden ausgelesen …"
          : upload.eintraege.length === 0
            ? "Hochladen"
            : upload.eintraege.length === 1
              ? "1 Datei hochladen"
              : `${upload.eintraege.length} Dateien hochladen`}
      </Button>
    </div>
  )
}

export function VerbisLebenslaufHochladen({
  statusError,
  ...bereich
}: VerbisLebenslaufBereichProps & {
  statusError: boolean
}) {
  const { done, hinweise, ohneDatum } = bereich
  const pruefen = hinweise.length > 0 || ohneDatum.length > 0
  return statusError ? (
    <Confirmation tone="error" title="Stand konnte nicht geladen werden">
      Der Stand konnte nicht geladen werden. Bitte schließen Sie die Box und öffnen Sie sie erneut.</Confirmation>
  ) : done.verbis && done.lebenslauf && !pruefen ? (
    <Confirmation tone="success" title="VerBIS und Lebenslauf ausgelesen">
      Die VerBIS-Screenshots und der Lebenslauf sind bereits gespeichert.</Confirmation>
  ) : (
    <FormScroll>
      <VerbisLebenslaufBereich {...bereich} />
    </FormScroll>
  )
}

export function VerbisLebenslaufBereich({
  done,
  statusLoading,
  uploading,
  eintraege,
  hinweise,
  ohneDatum,
  onAdd,
  onRemove,
  onSaveZeitraum,
  folgt = false,
  actions,
}: VerbisLebenslaufBereichProps) {
  const pruefen = hinweise.length > 0 || ohneDatum.length > 0
  return (
    <>
      {pruefen && <LebenslaufHinweise hinweise={hinweise} ohneDatum={ohneDatum} onSave={onSaveZeitraum} />}
      {!(done.verbis && done.lebenslauf) && (
        <FormSection label="VerBIS und Lebenslauf hochladen" compact={!pruefen && !folgt}>
          {!statusLoading && (
            <>
              <HaelfteStatus done={done.verbis} text="VerBIS-Screenshots" />
              <HaelfteStatus done={done.lebenslauf} text="Lebenslauf-Seiten" />
            </>
          )}
          <DropCard
            title="Screenshots hier ablegen"
            body="Auswählen oder hierher ziehen – weitere Dateien können Sie danach hinzufügen"
            accept={VERBIS_LEBENSLAUF_ACCEPT}
            multiple
            onFiles={onAdd}
            typeError={keinBildText}
            loading={uploading || statusLoading}
            loadingLabel={uploading ? HOCHLADEN_TEXT : "Der Stand wird geladen …"}
          />
          {eintraege.length > 0 && (
            <StackList aria-label="Ausgewählte Dateien">
              {eintraege.map((eintrag) => (
                <StackListItem
                  key={eintrag.id}
                  end={
                    <>
                      {eintrag.art && (
                        <KiLabel kind="generiert" title="Von der KI zugeordnet – bitte prüfen.">
                          {ART_TEXT[eintrag.art]}
                        </KiLabel>
                      )}
                      <StatusLine as="span">{groesseText(eintrag.file.size)}</StatusLine>
                      <Button
                        iconOnly
                        title={`${eintrag.file.name} entfernen`}
                        disabled={uploading}
                        onClick={() => onRemove(eintrag.id)}
                      >
                        <Icon name="i-x" />
                      </Button>
                    </>
                  }
                >
                  {eintrag.file.name}
                </StackListItem>
              ))}
            </StackList>
          )}
          {actions && <div className="section-actions">{actions}</div>}
        </FormSection>
      )}
    </>
  )
}

function HaelfteStatus({ done, text }: { done: boolean; text: string }) {
  return done ? (
    <StatusLine tone="success" icon={<Icon name="i-check" />}>
      {text}{" ausgelesen"}</StatusLine>
  ) : (
    <StatusLine>{text}{" fehlen noch"}</StatusLine>
  )
}
